import { ImageMime, VideoMime } from '../enums/files.enums';
import { StaticVideoType } from '../enums/machine-msg.enum';
import { DeliveryType, PitchType } from '../enums/pitches.enums';
import { AwsRegion } from '../enums/s3.enum';
import { CameraPosition } from '../enums/videos.enums';
import { Nullable } from './common/i-generics';
import { IFFMPEGMetadata } from './i-ffmpeg-metadata';
import { IGenerateVideoOptions } from './mlb-stats-api/i-generate-video-request';
import { IMlbPlayerCoordinate } from './mlb-stats-api/i-player';
import { MongoChildRecord } from './mongo/_base';

export interface IVideoError {
  msg: string;
  fatal: boolean;
}

interface IVideoMetadata extends Partial<IVideo> {
  url: string;
  mime_type: VideoMime;

  // todo: rename this to match whatever KG gives
  // when not provided, assume 1x for pitcher videos and 0.5x for fire videos (from FW <1.9)
  speed?: number;
}

export interface IVideoPlayback {
  video: IVideoMetadata;
  thumb: {
    url: string;
    mime_type: ImageMime;
  };
}

export const PLAYBACK_PLACEHOLDER: IVideoPlayback = {
  video: {
    url: '',
    mime_type: VideoMime.MP4,
    cap_size_0: 748,
    cap_size_1: 1090,
  },
  thumb: {
    url: '',
    mime_type: ImageMime.None,
  },
};

export interface IVideoFrameSpec {
  ReleasePixelX: number;
  ReleasePixelY: number;

  MoundPixelX: number;
  MoundPixelY: number;

  // in seconds
  ReleaseTime: number;

  /** computed as ReleaseTime * fps, rounded to the nearest integer, should be between 0 and n_frames */
  ReleaseFrame: number;

  /** ft, +ve => LHP, -ve => RHP, copied to matching criteria */
  ReleaseSide: number;

  /** ft */
  ReleaseHeight: number;

  /** positive value for resizing video as rendered by projector, should be assumed to be 1 if undefined */
  RenderScale?: number;
}

// relevant props per video for auto-matching/suggesting algo
export interface IVideoSuggestionCriteria {
  PitcherFullName: Nullable<string>;

  TeamName: Nullable<string>;

  DeliveryType: Nullable<DeliveryType>;

  /** ft, +ve => LHP, -ve => RHP */
  ReleaseSide: number;

  // vs pitch type
  PitchType: PitchType;

  // vs hitter side
  CameraPosition: Nullable<CameraPosition>;

  // if video has been associated to a Stats API pitcher
  mlb_pitcher: Nullable<IMlbPlayerCoordinate>;
}

export interface IVideoResolution {
  // resolution height
  cap_size_0: number;

  // resolution width
  cap_size_1: number;

  // color depth
  cap_size_2: number;
}

export type IVideoQualityCriteria = IVideoResolution & {
  // frames per second
  fps: number;
};

export interface IVideo
  extends
    IVideoSuggestionCriteria,
    IVideoQualityCriteria,
    IVideoFrameSpec,
    MongoChildRecord {
  static_type?: StaticVideoType;

  /** _id of the original video that this was cloned from, empty if video is not a clone */
  original_id?: string;

  /** should be deleted before being posted */
  tempID?: string;

  /** username of user who last modified this video */
  changer: string;

  readonly mime_type: VideoMime;
  // for S3
  readonly video_path: string;
  // for S3, if undefined, assume ca-central-1
  readonly video_region: AwsRegion | undefined;

  readonly VideoFileName: string;
  VideoTitle?: Nullable<string>;

  MoundPixelX: number;
  MoundPixelY: number;

  n_frames: number;

  ffmpeg: IFFMPEGMetadata;

  generate_options?: IGenerateVideoOptions;

  // if video is derived from a specific game's play/guid
  mlb_gamePk?: number;
  // if video is derived from a specific game's play/guid
  mlb_guid?: string;
}

export interface IVideoFrame {
  base64: string;
  frame: number;
  /** like 'raw' or 'preview' */
  mode: string;
  scale?: number;
}

type IVideoKey = keyof IVideo;

/** all the non-update-able fields go here */
export const VIDEO_IMMUTABLE_PROPS: IVideoKey[] = [
  '_id',
  'video_path',
  'mime_type',
  'original_id',
  'VideoFileName',
  '_created',
  '_createdby',
  '_changed',
];

export const VIDEO_CSV_DATES: IVideoKey[] = ['_created', '_changed'];

/** properties that can be exported to CSV for batch updating */
export const VIDEO_CSV_STRINGS: IVideoKey[] = [
  '_id',
  'VideoFileName',
  'VideoTitle',
  'PitcherFullName',
  'TeamName',
  'PitchType',
  'DeliveryType',
  'CameraPosition',
];

/** properties that can be exported to CSV for batch updating */
export const VIDEO_CSV_FLOATS: IVideoKey[] = [
  'ReleasePixelX',
  'ReleasePixelY',
  'MoundPixelX',
  'MoundPixelY',
  'ReleaseTime',
  'ReleaseSide',
  'ReleaseHeight',
];
